import { Button } from '@radix-ui/themes';
import React from 'react';
import { Flex, Spinner, TextField, TextArea } from '@radix-ui/themes';
import { api } from '../../services/api';
import { useStore } from '../../store/store';
import Icon from '@mdi/react';
import { mdiCreation } from '@mdi/js';
import { Message } from 'primereact/message';
import avatar from '../../assets/avatar2.png';

export const LocationCreation = ({ locations, setLocations, novelId }) => {
    const [locationName, setLocationName] = React.useState('');
    const [locationDescription, setLocationDescription] = React.useState('');
    const [isLoading, setIsLoading] = React.useState(false);
    const [editMode, setEditMode] = React.useState(false);
    const [editIndex, setEditIndex] = React.useState(-1);
    const [state, actions] = useStore();

    const resetForm = () => {
        setLocationName('');
        setLocationDescription('');
    };

    const addLocation = () => {
        setLocations([...locations, {
            name: locationName,
            description: locationDescription
        }]);
        resetForm();
    }

    const updateLocation = () => {
        const updatedLocations = [...locations];
        updatedLocations[editIndex] = {
            name: locationName, 
            description: locationDescription
        };
        setLocations(updatedLocations);
        resetForm();
        setEditMode(false);
        setEditIndex(-1);
    }

    const dismissChanges = () => {
        resetForm();
        setEditMode(false);
        setEditIndex(-1);
    }

    const loadLocationData = (index) => {
        const location = locations[index];
        setLocationName(location.name);
        setLocationDescription(location.description);
        setEditMode(true);
        setEditIndex(index);
    }

    const generateLocation = async () => {
        setIsLoading(true);
        try {
            const result = await api.generateLocations(novelId, 1, locations);
            // Since we're only generating one location at a time
            const generatedLocation = result.locations[0];

            setLocationName(generatedLocation.name);
            setLocationDescription(generatedLocation.description);

        } catch (error) {
            console.error("Error generating location:", error);
        } finally {
            setIsLoading(false);
        }
    }

    const deleteLocation = (index) => {
        const updatedLocations = [...locations];
        updatedLocations.splice(index, 1);
        setLocations(updatedLocations);
        
        // If we're editing the location that gets deleted, reset the form
        if (editMode && editIndex === index) {
            dismissChanges();
        } else if (editMode && editIndex > index) {
            // If we're editing a location that comes after the deleted one, adjust the index
            setEditIndex(editIndex - 1);
        }
    }

    const locationsMarkup = locations.map((location, index) => {
        return (
            <div 
                key={index} 
                className={'character-create-card'}
                onClick={() => loadLocationData(index)}
                style={{ cursor: 'pointer', border: editMode && editIndex === index ? '2px solid #6221ab' : 'none' }}
            >
                <div className={'character-create-card-name'}>{location.name}</div>
                <div className={'character-create-card-role'}>
                    {location.description?.substring(0, 50)}
                    {location.description?.length > 50 ? '...' : ''}
                </div>
                <button 
                    className="character-create-card-delete" 
                    onClick={(e) => {
                        e.stopPropagation();
                        deleteLocation(index);
                    }}
                >
                    &times;
                </button>
            </div>
        );
    });

    return (
        <div>
            <h1 style={{fontFamily: 'Poppins'}}>Locations</h1>

            {state.novels.length === 0 ?
                <Flex className="tutorial-container" style={{}}>
                    <Flex className={"tutorial-text"}>
                        <img src={avatar} alt="Avatar" className="tutorial-avatar" />
                        <div>
                            You only need to add locations and lore (in the next step) if you want the AI to generate the entire novel outline for you, so feel free to skip these two steps if you want to structure the novel yourself.
                            <br />
                        </div>
                    </Flex>
                </Flex> : null}

            {locations.length == 0 && <Message severity="info" text="Feel free to skip this step, as you can always add locations later and, while recommended, not neccessary to generate chapters." />}

            <Flex gap="20px" wrap="wrap">
                {locationsMarkup}
            </Flex>
            <br />
            <Flex direction="column" gap="10px">
                <Flex direction="row" gap="10px">
                    <div className={'character-textarea-wrapper'}>
                        <div className='details-label'>Location Name</div>
                        <TextField.Root
                            placeholder="Name"
                            value={locationName}
                            onChange={(e) => {setLocationName(e.target.value)}}
                            variant='soft'
                            className={'create-textfield'}
                        />
                    </div>
                </Flex>

                <Flex gap="30px">
                    <div className={'character-textarea-wrapper'} style={{width: '100%'}}>
                        <div className='details-label'>Description</div>
                        <TextArea 
                            className={'create-textarea character-textarea'} 
                            value={locationDescription} 
                            variant="soft" 
                            placeholder="Description" 
                            onChange={(e) => {setLocationDescription(e.target.value)}} 
                        />
                    </div>
                </Flex>
            </Flex>
            <br />
            <Flex gap="20px">
            {!editMode &&
                <Button 
                    onClick={() => {generateLocation()}}
                    disabled={isLoading}
                    style={{background: '#6221ab', cursor: 'pointer'}}
                >
                    {isLoading ? (
                        <Flex gap="2" align="center">
                            <Spinner size="small" />
                            <span>Generating...</span>
                        </Flex>
                    ) : (
                        <>
                            <Icon style={{transform: 'scale(0.8)'}} path={mdiCreation} size={1} />
                            {`Generate ${locations.length > 0 && !editMode ? 'another' : ''} Location`}
                        </>
                    )}
                </Button>
            }
                <Button onClick={editMode ? updateLocation : addLocation} style={{cursor: 'pointer', background: 'hsl(227deg 40.1% 38.09%)'}}>
                    {editMode ? 'Update Location' : 'Add Location'}
                </Button>
                {editMode && (
                    <Button onClick={dismissChanges} style={{cursor: 'pointer', background: '#ff000088'}}>
                        Dismiss Changes
                    </Button>
                )}
            </Flex>
            <br />
        </div>
    );
};

export default LocationCreation;